import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { LinkGlobal } from '../link/link.Global';
import { getDefaultLinkDisplayOptions } from '../link/link.DisplayOptions';
import { getItemPicAsset } from '../../utils/item';

export const FilterLink = props => {
  const {
    item,
    parentId,
    displayTypeLink,
    displayType,
    elementsToDisplay,
    filterId,
    fullTabId,
    canEdit,
    onDragOut,
    noteNumEnabled = false,
    noteNumPosition = 'top-right',
    noteNum = 0,
    onChangeNoteNum = null,
    itemsPerRow = 4,
    itemStyleConfig = null,
    reducedMinAvatarSize: reducedMinAvatarSizeProp = null,
    disableItemDrag = false,
  } = props;
  function getRandomClasses() {
    const classes = [];
    if (Math.random() < 0.5) classes.push('row-span-6');
    else classes.push('row-span-12');
    if (Math.random() < 0.5) classes.push('col-span-2');

    // Générer un nombre aléatoire entre 1 et 3 pour col-start
    const colStart = Math.floor(Math.random() * 3) + 1;
    classes.push(`col-start-${colStart}`);

    return classes.join(' ');
  }
  const [linkClasses] = useState(getRandomClasses());
  const isTouch = useSelector(state => state.global.isTouch);
  const items = useSelector(state => state.items);
  const fallbackElementsToDisplay = useMemo(() => {
    return getDefaultLinkDisplayOptions(displayType);
  }, [displayType]);
  const effectiveElementsToDisplay =
    elementsToDisplay ?? fallbackElementsToDisplay;
  const avatarElement = effectiveElementsToDisplay?.find(
    element => element.type === 'avatar',
  );
  const isAvatarShown = avatarElement ? avatarElement.checked === true : true;
  const safeItemsPerRow = Math.max(
    1,
    Math.min(12, Number.isFinite(Number(itemsPerRow)) ? Number(itemsPerRow) : 4),
  );

  const effectiveReducedMinAvatarSize =
    displayType === 'réduit'
      ? isAvatarShown
        ? Number.isFinite(Number(reducedMinAvatarSizeProp))
          ? Number(reducedMinAvatarSizeProp)
          : 96
        : 0
      : 0;

  const gapRem = 0.5;
  const widthCalc = `calc((100% - ${(safeItemsPerRow - 1) * gapRem}rem) / ${safeItemsPerRow})`;
  const itemStyle =
    displayType !== 'massonery'
      ? {
          flex: `1 1 ${widthCalc}`,
          minWidth: 0,
          boxSizing: 'border-box',
          // Keep enough room for the avatar + label in reduced mode.
          minHeight:
            displayType === 'réduit' && isAvatarShown
              ? Math.max(70, effectiveReducedMinAvatarSize + 14)
              : undefined,
        }
      : undefined;

  const styleConfig = itemStyleConfig || {};
  const caseType = styleConfig?.displayCase || 'case pleine largeur';
  const bgPicAsset = Number.isFinite(styleConfig?.bgPicIndex)
    ? getItemPicAsset(item, styleConfig.bgPicIndex)
    : null;
  const bgMaskAsset = Number.isFinite(styleConfig?.bgMaskPicIndex)
    ? getItemPicAsset(item, styleConfig.bgMaskPicIndex)
    : null;
  const bgPicUrl = bgPicAsset?.url || null;
  const bgMaskUrl = bgMaskAsset?.url || null;
  const bgSize =
    styleConfig?.bgPicFit === 'cover'
      ? 'cover'
      : styleConfig?.bgPicFit === 'stretch'
        ? '100% 100%'
        : styleConfig?.bgPicFit === 'contain'
          ? 'contain'
          : 'auto';
  const maskSize =
    styleConfig?.bgMaskFit === 'cover'
      ? 'cover'
      : styleConfig?.bgMaskFit === 'stretch'
        ? '100% 100%'
        : styleConfig?.bgMaskFit === 'fixed'
          ? 'auto'
          : 'contain';
  const maskPosition = styleConfig?.bgMaskFit === 'fixed' ? '0 0' : 'center';
  const maskRepeat = styleConfig?.bgMaskFit === 'fixed' ? 'repeat' : 'no-repeat';
  const useMask = caseType === 'masque' && Boolean(bgMaskUrl);
  const maskStyle = useMask
    ? styleConfig?.bgMaskInvert === true
      ? {
          WebkitMaskImage: `url(${bgMaskUrl}), linear-gradient(#fff 0 0)`,
          maskImage: `url(${bgMaskUrl}), linear-gradient(#fff 0 0)`,
          WebkitMaskRepeat: `${maskRepeat}, no-repeat`,
          maskRepeat: `${maskRepeat}, no-repeat`,
          WebkitMaskMode: 'luminance, luminance',
          maskMode: 'luminance, luminance',
          WebkitMaskSize: `${maskSize}, 100% 100%`,
          maskSize: `${maskSize}, 100% 100%`,
          WebkitMaskPosition: `${maskPosition}, 0 0`,
          maskPosition: `${maskPosition}, 0 0`,
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }
      : {
          WebkitMaskImage: `url(${bgMaskUrl})`,
          maskImage: `url(${bgMaskUrl})`,
          WebkitMaskRepeat: maskRepeat,
          maskRepeat: maskRepeat,
          WebkitMaskSize: maskSize,
          maskSize: maskSize,
          WebkitMaskPosition: maskPosition,
          maskPosition: maskPosition,
          WebkitMaskMode: 'luminance',
          maskMode: 'luminance',
        }
    : {};
  const bgColorStyle = styleConfig?.bgColor
    ? {
        backgroundColor:
          styleConfig.bgColor === 'transparent'
            ? 'transparent'
            : `rgb(var(--${styleConfig.bgColor}) / 0.35)`,
      }
    : {};
  const bgMaskRotate = Number.isFinite(styleConfig?.bgMaskRotate)
    ? styleConfig.bgMaskRotate
    : 0;
  const resolvedFullTabId = useMemo(() => {
    if (!fullTabId || fullTabId === 'sidebar') {
      return 'sidebar';
    }

    const tabIds = Array.isArray(item?.tabs)
      ? item.tabs.map(tabObj => Object.keys(tabObj)?.[0]).filter(Boolean)
      : [];

    // Direct hit: this item already has that tab id.
    if (tabIds.includes(fullTabId)) {
      return fullTabId;
    }

    // Label-based fallback:
    // - new explicit format: "label:<tab label>"
    // - legacy id format: reuse the source tab label and find same label here
    const labelFromToken = String(fullTabId).startsWith('label:')
      ? String(fullTabId).slice('label:'.length).trim()
      : '';
    const labelFromId =
      !labelFromToken && items?.[fullTabId]?.label
        ? String(items[fullTabId].label).trim()
        : '';
    const targetLabel = labelFromToken || labelFromId;

    if (!targetLabel) {
      return 'sidebar';
    }

    const matchedTabId = tabIds.find(tabId => {
      const tab = items?.[tabId];
      return tab?.label && String(tab.label).trim() === targetLabel;
    });

    return matchedTabId || 'sidebar';
  }, [fullTabId, item?.tabs, items]);

  return (
    <div
      className={`${linkClasses} board-grid-no-drag`}
      style={itemStyle}
      draggable={canEdit && !isTouch && !disableItemDrag}
      onDragStart={event => {
        if (!canEdit || isTouch || disableItemDrag) return;
        const payload = {
          type: 'item',
          itemId: item?._id,
          sourceFilterId: filterId ?? null,
        };
        event.dataTransfer.setData(
          'application/x-estynoss-item',
          JSON.stringify(payload),
        );
        event.dataTransfer.effectAllowed = 'move';
        window.__filterDropTargetId = null;
      }}
      onDragEnd={() => {
        if (!canEdit || isTouch || disableItemDrag) return;
        if (!window.__filterDropTargetId && onDragOut) {
          onDragOut(item?._id, filterId);
        }
        window.__filterDropTargetId = null;
      }}
    >
      <div
        className={`relative overflow-hidden h-full ${
          caseType === 'case arrondie' ? 'rounded p-05' : ''
        }`}
      >
        {(styleConfig?.bgColor || bgPicUrl) && (
          <div
            className={`absolute inset-0 ${caseType === 'case arrondie' ? 'rounded' : ''}`}
            style={{
              ...bgColorStyle,
              ...(maskStyle || null),
              transform:
                caseType === 'masque' && bgMaskRotate
                  ? `rotate(${bgMaskRotate}deg)`
                  : undefined,
            }}
          />
        )}
        {bgPicUrl && (
          <div
            className={`absolute inset-0 ${caseType === 'case arrondie' ? 'rounded' : ''}`}
            style={{
              backgroundImage: `url(${bgPicUrl})`,
              backgroundPosition: 'center',
              backgroundSize: bgSize,
              backgroundRepeat: 'no-repeat',
              ...(maskStyle || null),
              transform:
                caseType === 'masque' && bgMaskRotate
                  ? `rotate(${bgMaskRotate}deg)`
                  : undefined,
            }}
          />
        )}
        <div className='relative z-10 h-full'>
          <LinkGlobal
            key={item._id}
            linkId={item._id}
            itemId={parentId}
            displayType={displayTypeLink ?? 'link'}
            enableDrag={false}
            fullTabId={resolvedFullTabId}
            elementsToDisplay={effectiveElementsToDisplay}
            noteNumEnabled={noteNumEnabled}
            noteNumPosition={noteNumPosition}
            noteNum={noteNum}
            onChangeNoteNum={onChangeNoteNum}
            noteCanEdit={canEdit}
            reducedMinAvatarSize={effectiveReducedMinAvatarSize}
          />
        </div>
      </div>
    </div>
  );
};
